import { useState, useEffect, useCallback } from "react";
import { UtensilsCrossed, Users, CreditCard, ArrowRight, Pencil, X, Save, Upload, Minus, Plus, ShoppingCart, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU_CARD_URL = "https://customer-assets.emergentagent.com/job_cc551d47-83ea-4071-81f7-ae5cb0c54e08/artifacts/ihxiegp7_C115B5F0-CB15-461B-B732-2210421DEC47.png";

const ALACARTE_MENU = [
  { id: "pomfritter", name: "Pomfritter", price: 25 },
  { id: "kylling", name: "Kylling Kugler m. Sursød Sovs & Pomfritter", price: 40 },
  { id: "hotwings", name: "Hot Wings & Pomfritter", price: 40 },
  { id: "nuggets", name: "Nugges & Pomfritter", price: 40 },
  { id: "fishnchips", name: "Fish and Chips", price: 40 },
  { id: "panini", name: "Panini", price: 35 },
];

const BordbestillingPage = () => {
  const navigate = useNavigate();
  const { isAdmin, isModerator, getToken } = useAuth();
  const [name, setName] = useState("");
  const [dinnerQty, setDinnerQty] = useState(0);
  const [childQty, setChildQty] = useState(0);
  const [alacarteQty, setAlacarteQty] = useState({});
  const [loading, setLoading] = useState(false);
  const [tuesdayInfo, setTuesdayInfo] = useState(null);
  const [cms, setCms] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showMenuCard, setShowMenuCard] = useState(false);

  const canEdit = isAdmin || isModerator;

  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  useEffect(() => {
    axios.get(`${API}/food-orders/next-tuesday`).then(r => setTuesdayInfo(r.data)).catch(() => {});
    axios.get(`${API}/settings/bordbestilling`).then(r => setCms(r.data)).catch(() => {});
  }, []);

  const price = tuesdayInfo?.price || cms?.dinner_price || 60;

  // Calculate totals
  const childPrice = Math.round(price / 2);
  const dinnerTotal = dinnerQty * price;
  const childTotal = childQty * childPrice;
  const alacarteTotal = ALACARTE_MENU.reduce((sum, item) => sum + (alacarteQty[item.id] || 0) * item.price, 0);
  const grandTotal = dinnerTotal + childTotal + alacarteTotal;
  const hasItems = grandTotal > 0;
  const alacarteItemsSelected = ALACARTE_MENU.filter(i => (alacarteQty[i.id] || 0) > 0);

  const updateAlacarte = (id, delta) => {
    setAlacarteQty(prev => ({ ...prev, [id]: Math.max(0, Math.min(20, (prev[id] || 0) + delta)) }));
  };

  const handlePayment = async () => {
    if (!name.trim()) { toast.error("Indtast dit navn"); return; }
    if (!hasItems) { toast.error("Vælg mindst én vare"); return; }
    setLoading(true);
    try {
      const alacarte = alacarteItemsSelected.map(item => ({
        name: item.name,
        price: item.price,
        qty: alacarteQty[item.id],
      }));
      const res = await axios.post(`${API}/food-orders/create-payment`, {
        name: name.trim(),
        quantity: dinnerQty,
        child_quantity: childQty,
        alacarte,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke starte betaling");
      setLoading(false);
    }
  };

  const openEdit = () => { setEditData({ ...cms }); setEditing(true); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/bordbestilling`, editData, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      });
      setCms(res.data);
      setEditing(false);
      toast.success("Indhold opdateret!");
      axios.get(`${API}/food-orders/next-tuesday`).then(r => setTuesdayInfo(r.data));
    } catch { toast.error("Kunne ikke gemme"); }
    finally { setSaving(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/events/upload-image`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      const url = res.data.url || res.data.file_url || res.data.path;
      setEditData(prev => ({ ...prev, hero_image: url }));
      toast.success("Billede uploadet!");
    } catch { toast.error("Kunne ikke uploade billede"); }
    finally { setUploadingImage(false); }
  };

  const heroImg = cms?.hero_image;
  const defaultHero = "https://images.unsplash.com/photo-1587824622306-deac8a649f37?w=1200";

  if (!cms) return null;

  return (
    <div className="min-h-screen" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(30,30,30,1) 0%, rgba(10,10,10,1) 60%)" }}>

      {/* ===== HERO SECTION ===== */}
      <div className="relative h-[40vh] sm:h-[50vh] min-h-[300px] overflow-hidden" data-testid="food-hero">
        <img src={heroImg || defaultHero} alt="Bordbestilling" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30"></div>
        <button onClick={() => navigate(-1)} className="absolute top-20 left-4 z-30 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/40 backdrop-blur-sm text-gray-300 hover:text-white text-sm transition-all" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Tilbage
        </button>
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-8 sm:pb-12 px-4 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Oswald'] font-black text-white tracking-wider mb-2 drop-shadow-2xl" data-testid="bordbestilling-title">
            {cms.hero_title}
          </h1>
          <p className="text-gray-300 text-base sm:text-lg max-w-lg">{cms.hero_subtitle}</p>
        </div>
        {canEdit && !editing && (
          <button onClick={openEdit}
            className="absolute top-20 right-4 z-30 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/20 backdrop-blur-sm border border-amber-500/50 text-amber-400 hover:text-amber-300 hover:bg-amber-500/30 transition-all font-['Oswald'] uppercase tracking-wider text-sm"
            data-testid="food-edit-btn">
            <Pencil size={16} /> Rediger
          </button>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-4 pb-16 -mt-4 relative z-10">

        {/* ===== CMS EDITOR MODAL ===== */}
        {editing && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="food-edit-modal">
            <div className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-['Oswald'] font-bold text-lg">Rediger Bordbestilling</h3>
                <button onClick={() => setEditing(false)} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">Hero billede</label>
                  <div className="flex gap-2">
                    <input value={editData.hero_image || ""} onChange={e => setEditData(p => ({ ...p, hero_image: e.target.value }))}
                      placeholder="Billede-URL eller upload" className="flex-1 bg-black/40 border border-gray-700 text-white text-sm rounded px-3 py-2 focus:outline-none focus:border-amber-500/50" />
                    <label className="px-3 py-2 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 cursor-pointer text-sm flex items-center gap-1">
                      <Upload className="w-4 h-4" /> {uploadingImage ? "..." : "Upload"}
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    </label>
                  </div>
                </div>
                {[["hero_title", "Overskrift"], ["hero_subtitle", "Undertekst"]].map(([key, label]) => (
                  <div key={key}>
                    <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">{label}</label>
                    <input value={editData[key] || ""} onChange={e => setEditData(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full bg-black/40 border border-gray-700 text-white text-sm rounded px-3 py-2 focus:outline-none focus:border-amber-500/50" />
                  </div>
                ))}
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">Info-tekst</label>
                  <textarea value={editData.info_text || ""} onChange={e => setEditData(p => ({ ...p, info_text: e.target.value }))}
                    rows={3} className="w-full bg-black/40 border border-gray-700 text-white text-sm rounded px-3 py-2 resize-none focus:outline-none focus:border-amber-500/50" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">Dagens ret (vises på forsiden)</label>
                  <input value={editData.dagens_ret || ""} onChange={e => setEditData(p => ({ ...p, dagens_ret: e.target.value }))}
                    placeholder="F.eks. Lasagne med salat"
                    className="w-full bg-black/40 border border-gray-700 text-white text-sm rounded px-3 py-2 focus:outline-none focus:border-amber-500/50" data-testid="food-edit-dagens-ret" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">Middag titel</label>
                    <input value={editData.dinner_title || ""} onChange={e => setEditData(p => ({ ...p, dinner_title: e.target.value }))}
                      className="w-full bg-black/40 border border-gray-700 text-white text-sm rounded px-3 py-2 focus:outline-none focus:border-amber-500/50" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">Pris (DKK)</label>
                    <input type="number" value={editData.dinner_price || ""} onChange={e => setEditData(p => ({ ...p, dinner_price: parseFloat(e.target.value) || 0 }))}
                      className="w-full bg-black/40 border border-gray-700 text-white text-sm rounded px-3 py-2 focus:outline-none focus:border-amber-500/50" data-testid="food-edit-price" />
                  </div>
                </div>
              </div>
              <button onClick={saveEdit} disabled={saving}
                className="w-full mt-4 py-3 rounded-xl font-['Oswald'] font-bold uppercase tracking-wider bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
                data-testid="food-edit-save">
                <Save className="w-5 h-5" /> {saving ? "Gemmer..." : "Gem ændringer"}
              </button>
            </div>
          </div>
        )}

        {/* ===== INFO ===== */}
        <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-xl p-5 sm:p-6 mb-5" data-testid="food-info">
          <p className="text-gray-300 leading-relaxed text-sm sm:text-base">{cms.info_text}</p>
          {cms.dagens_ret && cms.dagens_ret !== "Annonceres snart" && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-amber-400/70 text-xs uppercase tracking-wider font-['Oswald'] mb-1">Dagens ret</p>
              <p className="text-white font-['Oswald'] font-bold text-lg" data-testid="dagens-ret-display">{cms.dagens_ret}</p>
            </div>
          )}
        </div>

        {/* ===== NAME INPUT ===== */}
        <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-xl p-5 sm:p-6 mb-5">
          <label className="text-gray-400 text-xs uppercase tracking-wider mb-1.5 block font-['Oswald']">Dit navn</label>
          <input
            type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="Indtast dit navn"
            className="w-full bg-black/40 border border-gray-700 text-white text-sm rounded-xl px-4 py-3 focus:border-amber-500/50 focus:outline-none placeholder-gray-600"
            data-testid="food-order-name"
          />
        </div>

        {/* ===== TUESDAY DINNER SECTION ===== */}
        <div id="bestil" className="bg-gradient-to-br from-amber-900/20 to-amber-800/5 border-2 border-amber-500/30 rounded-2xl p-5 sm:p-6 mb-5"
          style={{ boxShadow: "0 0 30px rgba(212,175,55,0.1)" }} data-testid="tuesday-dinner-card">

          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="text-xl sm:text-2xl font-['Oswald'] font-bold text-white tracking-wide">
                {cms.dinner_title}
              </h2>
              {tuesdayInfo && (
                <p className="text-amber-400/70 text-xs mt-0.5">Næste tirsdag: {tuesdayInfo.event_date_formatted}</p>
              )}
            </div>
            <div className="text-right">
              <span className="text-2xl sm:text-3xl font-['Oswald'] font-black text-amber-400">{price}</span>
              <span className="text-amber-400/70 text-sm ml-1">kr/pers</span>
            </div>
          </div>

          {/* Order count */}
          {tuesdayInfo && tuesdayInfo.orders_count > 0 && (
            <div className="flex items-center gap-2 mb-4 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid="orders-count">
              <Users className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-emerald-400 font-bold text-sm">{tuesdayInfo.orders_count}</span>
              <span className="text-emerald-400/70 text-xs">har allerede bestilt</span>
            </div>
          )}

          {/* Dinner quantity selector */}
          <div className="flex items-center justify-between">
            <span className="text-gray-300 text-sm">Voksne (12+ år)</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setDinnerQty(q => Math.max(0, q - 1))} disabled={dinnerQty <= 0}
                className="w-9 h-9 rounded-lg bg-black/40 border border-gray-700 text-white flex items-center justify-center hover:border-amber-500/50 disabled:opacity-30 transition-all"
                data-testid="dinner-qty-minus">
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-xl font-['Oswald'] font-black text-white w-8 text-center" data-testid="dinner-qty-value">{dinnerQty}</span>
              <button onClick={() => setDinnerQty(q => Math.min(20, q + 1))}
                className="w-9 h-9 rounded-lg bg-black/40 border border-gray-700 text-white flex items-center justify-center hover:border-amber-500/50 transition-all"
                data-testid="dinner-qty-plus">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          {dinnerTotal > 0 && (
            <div className="text-right mt-1">
              <span className="text-amber-400/70 text-xs">{dinnerQty} x {price} kr = {dinnerTotal} kr</span>
            </div>
          )}

          {/* Child quantity selector */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
            <div>
              <span className="text-gray-300 text-sm">Børn (0-11 år)</span>
              <span className="text-amber-400/60 text-xs ml-2">halv pris</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setChildQty(q => Math.max(0, q - 1))} disabled={childQty <= 0}
                className="w-9 h-9 rounded-lg bg-black/40 border border-gray-700 text-white flex items-center justify-center hover:border-amber-500/50 disabled:opacity-30 transition-all"
                data-testid="child-qty-minus">
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-xl font-['Oswald'] font-black text-white w-8 text-center" data-testid="child-qty-value">{childQty}</span>
              <button onClick={() => setChildQty(q => Math.min(20, q + 1))}
                className="w-9 h-9 rounded-lg bg-black/40 border border-gray-700 text-white flex items-center justify-center hover:border-amber-500/50 transition-all"
                data-testid="child-qty-plus">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          {childTotal > 0 && (
            <div className="text-right mt-1">
              <span className="text-amber-400/70 text-xs">{childQty} x {childPrice} kr = {childTotal} kr</span>
            </div>
          )}
        </div>

        {/* ===== À LA CARTE SECTION ===== */}
        <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-5 sm:p-6 mb-5" data-testid="alacarte-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl sm:text-2xl font-['Oswald'] font-bold text-white tracking-wide">
              À la carte
            </h2>
            <button onClick={() => setShowMenuCard(true)} className="text-amber-400 text-xs border border-amber-500/30 rounded-lg px-3 py-1.5 hover:bg-amber-500/10 transition-all" data-testid="show-menucard-btn">
              Se menukort
            </button>
          </div>

          <div className="space-y-2">
            {ALACARTE_MENU.map(item => {
              const qty = alacarteQty[item.id] || 0;
              return (
                <div key={item.id}
                  className={`flex items-center justify-between p-3 rounded-xl transition-all ${qty > 0 ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-black/20 border border-white/5'}`}
                  data-testid={`alacarte-item-${item.id}`}>
                  <div className="flex-1 min-w-0 mr-3">
                    <p className={`text-sm font-medium ${qty > 0 ? 'text-amber-300' : 'text-gray-300'}`}>{item.name}</p>
                    <p className="text-amber-400/70 text-xs">{item.price} kr</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {qty > 0 && (
                      <button onClick={() => updateAlacarte(item.id, -1)}
                        className="w-8 h-8 rounded-lg bg-black/40 border border-gray-700 text-white flex items-center justify-center hover:border-red-500/50 transition-all"
                        data-testid={`alacarte-minus-${item.id}`}>
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {qty > 0 && (
                      <span className="text-white font-bold text-sm w-6 text-center">{qty}</span>
                    )}
                    <button onClick={() => updateAlacarte(item.id, 1)}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${qty > 0 ? 'bg-amber-500/20 border border-amber-500/30 text-amber-400' : 'bg-black/40 border border-gray-700 text-gray-400 hover:border-amber-500/50 hover:text-amber-400'}`}
                      data-testid={`alacarte-plus-${item.id}`}>
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {alacarteTotal > 0 && (
            <div className="text-right mt-3 pt-3 border-t border-white/10">
              <span className="text-amber-400 text-sm font-bold">À la carte: {alacarteTotal} kr</span>
            </div>
          )}
        </div>

        {/* ===== ORDER SUMMARY & PAY ===== */}
        {hasItems && (
          <div className="bg-gradient-to-br from-emerald-900/20 to-emerald-800/5 border-2 border-emerald-500/30 rounded-2xl p-5 sm:p-6 mb-5"
            style={{ boxShadow: "0 0 20px rgba(52,211,153,0.1)" }} data-testid="order-summary">
            <h3 className="text-lg font-['Oswald'] font-bold text-white tracking-wide mb-3 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-emerald-400" /> Din bestilling
            </h3>
            <div className="space-y-1.5 text-sm mb-4">
              {dinnerTotal > 0 && (
                <div className="flex justify-between text-gray-300">
                  <span>Middag voksne ({dinnerQty} pers.)</span>
                  <span className="text-white font-medium">{dinnerTotal} kr</span>
                </div>
              )}
              {childTotal > 0 && (
                <div className="flex justify-between text-gray-300">
                  <span>Middag børn ({childQty} pers.)</span>
                  <span className="text-white font-medium">{childTotal} kr</span>
                </div>
              )}
              {alacarteItemsSelected.map(item => (
                <div key={item.id} className="flex justify-between text-gray-300">
                  <span>{item.name} x{alacarteQty[item.id]}</span>
                  <span className="text-white font-medium">{item.price * alacarteQty[item.id]} kr</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t border-white/10 text-base">
                <span className="text-white font-bold">Total</span>
                <span className="text-amber-400 font-black text-lg">{grandTotal} kr</span>
              </div>
            </div>
          </div>
        )}

        {/* Pay button */}
        <button onClick={handlePayment} disabled={loading || !name.trim() || !hasItems}
          className="w-full py-4 rounded-xl font-['Oswald'] font-bold text-base uppercase tracking-wider bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 shadow-lg mb-5"
          style={{ boxShadow: hasItems ? "0 4px 20px rgba(212,175,55,0.3)" : "none" }}
          data-testid="food-pay-btn">
          <CreditCard className="w-5 h-5" />
          {loading ? "Starter betaling..." : hasItems ? `Betal ${grandTotal} kr` : "Vælg mad ovenfor"}
          {!loading && hasItems && <ArrowRight className="w-5 h-5" />}
        </button>
      </div>

      {/* Menukort Modal */}
      {showMenuCard && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setShowMenuCard(false)} data-testid="menukort-modal">
          <div className="relative max-w-4xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowMenuCard(false)}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full w-12 h-12 flex items-center justify-center text-2xl"
              data-testid="close-menukort-btn">
              ×
            </button>
            <img src={MENU_CARD_URL} alt="Menukort" className="w-full h-auto rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default BordbestillingPage;
